import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaChevronDown,
  FaInfoCircle,
  FaTimes,
  FaUserCheck,
  FaUserPlus,
} from "react-icons/fa";
import "../assets/css/AddTenant.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/AddLead.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import { getAuthItem } from "../utils/authStorage";
import { formatPascal } from "../utils/formatters";
import {
  DEFAULT_COUNTRY_CODE,
  normalizeCountryCode,
  toLocalPhone,
  formValueWithMobileSanitize,
  mobileValidationMessage,
} from "../utils/phone";
import { useConfirm } from "../components/ConfirmModal";
import {
  PORTAL_OWN_DOMAIN,
  PORTAL_SHARED,
  normalizePortalDomain,
  tenantLoginEmailError,
} from "../utils/loginDomain";
import OwnerPasswordField, { generatePassword } from "../components/OwnerPasswordField";
import TenantSourceField from "../components/TenantSourceField";
import AddressSearch from "../components/AddressSearch";
import useAddressAutofill from "../utils/useAddressAutofill";
import PortalLoginFields, {
  autoOwnerEmail,
  ownerDomainError,
  portalHostFor,
  ownerEmailHint,
  ownerEmailPlaceholder,
  portalSettingsError,
} from "../components/PortalLoginFields";
import useOwnerLookup, { describeExistingOwner, OWNER_EMAIL_INFO } from "../utils/useOwnerLookup";
import ServicePicker from "../components/ServicePicker";

const CUSTOMER_STATUSES = ["Active", "Inactive"];
const CUSTOMER_TYPES = ["Organization", "Individual"];
const BUSINESS_MODELS = [
  { value: "Project", label: "Project Based (e.g. builders, developers)" },
  { value: "Service", label: "Service Based (e.g. stores, food, studios, agencies)" },
];

function domainAdminLogin(portalType, portalDomain) {
  if (portalType !== PORTAL_OWN_DOMAIN) return "";
  const domain = normalizePortalDomain(portalDomain);
  if (!domain || portalSettingsError(portalType, domain)) return "";
  return `admin@${domain}`;
}

function AddTenant({ onBack, onAddTenant }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [formData, setFormData] = useState({
    customerType: "Individual",
    firstName: "",
    lastName: "",
    personalEmail: "",
    personalPhone: "",
    altPhone: "",
    aadhar: "",
    companyName: "",
    domain: "",
    businessModel: "",
    companyEmail: "",
    companyPhone: "",
    website: "",
    websiteDomainName: "",
    source: "",
    status: "Active",
    communicationLanguage: "",
    isQuoteSent: false,
    portalPassword: "",
    ownerCompanyEmail: "",
    portalType: PORTAL_SHARED,
    portalDomain: "",
    services: [],
    companyAddressLine1: "",
    companyAddressLine2: "",
    companyCity: "",
    companyState: "",
    companyCountry: "",
    companyZipCode: "",
    notes: ""
  });

  const [submitting, setSubmitting] = useState(false);
  // Existing login for the owner email: that person becomes owner here too (no new password).
  const existingOwner = useOwnerLookup(formData.ownerCompanyEmail);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [isPrimaryCompany, setIsPrimaryCompany] = useState(false);
  // A Customer Domain company gets an admin@<domain> owner login. An existing owner keeps their own
  // login only when it is already on that domain.
  const ownerLoginFor = (owner, portalType, portalDomain) => {
    const admin = domainAdminLogin(portalType, portalDomain);
    const current = owner?.Company_Email || "";
    if (!admin) return current;
    return current.toLowerCase().endsWith(`@${admin.split("@")[1]}`) ? current : admin;
  };
  const domainAdminEmail = domainAdminLogin(formData.portalType, formData.portalDomain);
  const isDomainAdminLogin = Boolean(
    domainAdminEmail && formData.ownerCompanyEmail.trim().toLowerCase() === domainAdminEmail
  );
  const ownerNeedsDomainLogin = Boolean(
    selectedOwner &&
      ownerLoginFor(selectedOwner, formData.portalType, formData.portalDomain).toLowerCase() !==
        String(selectedOwner.Company_Email || "").toLowerCase()
  );
  const domainAdminNote = isDomainAdminLogin
    ? `Admin account for ${normalizePortalDomain(formData.portalDomain)} is created on save.${
        ownerNeedsDomainLogin && selectedOwner?.Company_Email
          ? ` ${selectedOwner.Company_Email} stays as is.`
          : ""
      }`
    : "";
  const currentPrimaryCompany = selectedOwner?.businesses?.find((b) => b.Is_Primary)?.Company_Name || "";
  const [ownerResults, setOwnerResults] = useState([]);
  const [ownerSearching, setOwnerSearching] = useState(false);
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const ownerPickerRef = useRef(null);
  // Only fields the selected owner already has are locked; missing ones stay editable.
  const lockedPersonFields = useMemo(
    () => ({
      lastName: Boolean(selectedOwner?.Last_Name),
      personalEmail: Boolean(selectedOwner?.Email),
      personalPhone: Boolean(selectedOwner?.Phone),
    }),
    [selectedOwner]
  );
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [companyStates, setCompanyStates] = useState([]);
  const [companyCities, setCompanyCities] = useState([]);
  const [selectedCompanyCountryId, setSelectedCompanyCountryId] = useState(null);
  const [selectedCompanyStateId, setSelectedCompanyStateId] = useState(null);
  const addressAutofill = useAddressAutofill({
    countries,
    companyStates,
    companyCities,
    selectedCompanyCountryId,
    selectedCompanyStateId,
    setFormData,
    setSelectedCompanyCountryId,
    setSelectedCompanyStateId,
    setCompanyStates,
    setCompanyCities,
  });
  const [customServices, setCustomServices] = useState({});
  const [editingService, setEditingService] = useState(null);
  const [isAddingServiceToCategory, setIsAddingServiceToCategory] = useState(null);
  const [personalPhoneCountryCode, setPersonalPhoneCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [altPhoneCountryCode, setAltPhoneCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [companyPhoneCountryCode, setCompanyPhoneCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const domainDropdownRef = useRef(null);

  useEffect(() => {
    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        const res = await axiosInstance.get("/service/categories");
        if (res.data && Array.isArray(res.data)) setServiceCategories(res.data);
      } catch (err) {
        console.error("Error fetching services:", err);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const res = await axiosInstance.get("/countries");
        if (res.data && Array.isArray(res.data)) setCountries(res.data);
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  // Fetch domains for dropdown (same API as filter section)
  useEffect(() => {
    const fetchDomains = async () => {
      setLoadingDomains(true);
      try {
        const res = await axiosInstance.get("/domains");
        const raw = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        const mapped = raw
          .map((d) => ({
            id: d.Domain_Id ?? d.domain_id ?? d.id,
            name: d.Domain_Name ?? d.domain_name ?? d.name ?? "",
            defaultModel: d.Default_Business_Model || ""
          }))
          .filter((d) => d.id != null && d.name.trim() !== "");
        setDomains(mapped);
      } catch (err) {
        console.error("Error fetching domains for customer:", err);
        setDomains([]);
      } finally {
        setLoadingDomains(false);
      }
    };
    fetchDomains();
  }, []);



  useEffect(() => {
    if (!selectedCompanyCountryId) {
      setCompanyStates([]);
      return;
    }
    axiosInstance
      .get(`/states/${selectedCompanyCountryId}`)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) setCompanyStates(res.data);
      })
      .catch(() => setCompanyStates([]));
  }, [selectedCompanyCountryId]);

  useEffect(() => {
    if (!selectedCompanyStateId) {
      setCompanyCities([]);
      return;
    }
    axiosInstance
      .get(`/cities/${selectedCompanyStateId}`)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) setCompanyCities(res.data);
      })
      .catch(() => setCompanyCities([]));
  }, [selectedCompanyStateId]);


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) { setDomainDropdownOpen(false); setDomainSearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const q = formData.firstName.trim();
    if (selectedOwner || q.length < 2) {
      setOwnerResults([]);
      setOwnerSearching(false);
      return undefined;
    }
    let active = true;
    setOwnerSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axiosInstance.get("/owner-search", { params: { q } });
        if (active) setOwnerResults(Array.isArray(res.data?.owners) ? res.data.owners : []);
      } catch (_) {
        if (active) setOwnerResults([]);
      } finally {
        if (active) setOwnerSearching(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [formData.firstName, selectedOwner]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ownerPickerRef.current && !ownerPickerRef.current.contains(e.target)) setOwnerDropdownOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const autoPasswordForRef = useRef("");
  useEffect(() => {
    if (!isDomainAdminLogin || existingOwner || autoPasswordForRef.current === domainAdminEmail) return;
    autoPasswordForRef.current = domainAdminEmail;
    setFormData((prev) => (prev.portalPassword ? prev : { ...prev, portalPassword: generatePassword() }));
  }, [isDomainAdminLogin, existingOwner, domainAdminEmail]);

  const clearOwnerFields = () =>
    setFormData((prev) => ({
      ...prev,
      firstName: "",
      lastName: "",
      personalEmail: "",
      personalPhone: "",
      ownerCompanyEmail: "",
      portalPassword: "",
    }));

  const selectOwner = (owner) => {
    setSelectedOwner(owner);
    setIsPrimaryCompany(false);
    setOwnerDropdownOpen(false);
    setFormData((prev) => ({
      ...prev,
      firstName: owner.First_Name || "",
      lastName: owner.Last_Name || "",
      personalEmail: owner.Email || "",
      personalPhone: toLocalPhone(owner.Phone) || "",
      ownerCompanyEmail: ownerLoginFor(owner, prev.portalType, prev.portalDomain),
      portalPassword: "",
    }));
    setPersonalPhoneCountryCode(normalizeCountryCode(owner.Country_Code));
  };

  const clearSelectedOwner = () => {
    setSelectedOwner(null);
    setIsPrimaryCompany(false);
    clearOwnerFields();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "companyCountry") {
      const c = countries.find((x) => x.country_name === value);
      if (c) setSelectedCompanyCountryId(c.country_id);
      else {
        setSelectedCompanyCountryId(null);
        setSelectedCompanyStateId(null);
        setCompanyStates([]);
        setCompanyCities([]);
        setFormData((prev) => ({ ...prev, companyState: "", companyCity: "" }));
      }
    }
    if (name === "companyState") {
      const s = companyStates.find((x) => x.state_name === value);
      if (s) setSelectedCompanyStateId(s.state_id);
      else {
        setSelectedCompanyStateId(null);
        setCompanyCities([]);
        setFormData((prev) => ({ ...prev, companyCity: "" }));
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : formValueWithMobileSanitize(name, value)
    }));
  };

  const handleSaveService = () => {
    if (!editingService || !editingService.name.trim()) return;
    setCustomServices((prev) => ({
      ...prev,
      [editingService.id]: { name: editingService.name.trim(), categoryId: editingService.categoryId }
    }));
    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, editingService.id]
    }));
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleCancelEditService = () => {
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleDeleteCustomService = async (id) => {
    if (!(await confirm({ title: "Remove Service", message: "Remove this custom service?" }))) return;
    setCustomServices((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s !== id)
    }));
  };

  const renderCountryCodeSelect = (value, onChange, { disabled = false } = {}) => (
    <select
      style={{ width: "72px", minWidth: "72px", maxWidth: "72px", flex: "0 0 72px" }}
      value={value || DEFAULT_COUNTRY_CODE}
      onChange={(e) => onChange(normalizeCountryCode(e.target.value || DEFAULT_COUNTRY_CODE))}
      disabled={loadingCountries || disabled}
    >
      {countries.map((country) => {
        const code = (country.country_code || country.country_short_code || "").trim();
        if (!code) return null;
        return (
          <option key={country.country_id} value={normalizeCountryCode(code)}>
            {normalizeCountryCode(code)}
          </option>
        );
      })}
      {!countries.some((c) => normalizeCountryCode(c.country_code || c.country_short_code || "") === DEFAULT_COUNTRY_CODE) && (
        <option value={DEFAULT_COUNTRY_CODE}>{DEFAULT_COUNTRY_CODE}</option>
      )}
    </select>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.personalEmail) {
      alert("Please fill in required fields (First Name, Last Name, Personal Email, and Phone).");
      return;
    }
    const phoneError = mobileValidationMessage(formData.personalPhone, { required: true, label: "Phone" });
    if (phoneError) {
      alert(phoneError);
      return;
    }
    const altError = mobileValidationMessage(formData.altPhone, { label: "Alternate phone" });
    if (altError) {
      alert(altError);
      return;
    }
    const companyPhoneError = mobileValidationMessage(formData.companyPhone, { label: "Company phone" });
    if (companyPhoneError) {
      alert(companyPhoneError);
      return;
    }
    if (
      String(formData.customerType || "").toLowerCase() === "organization" &&
      !String(formData.companyName || "").trim()
    ) {
      alert("Please fill in required fields (Company Name is required for Organization).");
      return;
    }
    const ownerCompanyEmail = String(formData.ownerCompanyEmail || "").trim();
    if (!ownerCompanyEmail) {
      alert("Please fill in required fields (Owner Login Email is required).");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerCompanyEmail)) {
      alert("Enter a valid Owner Login Email.");
      return;
    }
    const portalError = portalSettingsError(formData.portalType, formData.portalDomain);
    if (portalError) {
      alert(portalError);
      return;
    }
    const portalDomain = normalizePortalDomain(formData.portalDomain);
    const ownerDomainMessage = ownerDomainError(ownerCompanyEmail, formData.portalType, portalDomain);
    if (ownerDomainMessage) {
      alert(ownerDomainMessage);
      return;
    }
    if (!existingOwner) {
      const ownerEmailError = tenantLoginEmailError(ownerCompanyEmail, formData.portalType, portalDomain);
      if (ownerEmailError) {
        alert(ownerEmailError);
        return;
      }
    }
    if (!existingOwner && !String(formData.portalPassword || "").trim()) {
      alert("Please fill in required fields (Password is required).");
      return;
    }
    if (!formData.services || formData.services.length === 0) {
      alert("Please select a service and at least one of its sub-services.");
      return;
    }
    if (!String(formData.domain || "").trim()) {
      alert("Please select a Business.");
      return;
    }
    if (!formData.businessModel) {
      alert("Please select the Business Model (Project Based or Service Based).");
      return;
    }

    let createdBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }

    const selectedCompanyCountry = countries.find((c) => c.country_name === formData.companyCountry);
    const selectedCompanyState = companyStates.find((s) => s.state_name === formData.companyState);
    const selectedCompanyCity = companyCities.find((c) => c.city_name === formData.companyCity);

    const numericServiceIds = formData.services.filter((s) => typeof s === "number");
    const customServiceNames = formData.services
      .filter((s) => typeof s === "string")
      .map((id) => customServices[id]?.name)
      .filter(Boolean);

    const rawDomain = (formData.domain || "").toString().trim();
    let domainName = null;
    let domainId = null;
    if (rawDomain) {
      const match = domains.find((d) => String(d.id) === rawDomain);
      if (match) {
        domainName = (match.name || "").trim() || null;
        domainId = Number(match.id);
      } else if (/^\d+$/.test(rawDomain)) {
        domainId = parseInt(rawDomain, 10);
      } else {
        domainName = rawDomain;
      }
    }

    const payload = {
      Customer_Type: formData.customerType || "Individual",
      First_Name: formatPascal(formData.firstName),
      Last_Name: formatPascal(formData.lastName),
      Email: formData.personalEmail,
      Country_Code: normalizeCountryCode(personalPhoneCountryCode),
      Phone: toLocalPhone(formData.personalPhone),
      Alt_Phone: toLocalPhone(formData.altPhone),
      Aadhar: (formData.aadhar || "").trim() || null,
      Company_Email: formData.companyEmail || null,
      Company_Name: formData.companyName ? formatPascal(formData.companyName) : null,
      Company_Phone: toLocalPhone(formData.companyPhone),
      Domain: domainName,
      ...(domainId != null && Number.isFinite(domainId) ? { Domain_Id: domainId } : {}),
      Business_Model: formData.businessModel,
      Source: String(formData.source || "").trim() || null,
      Status: formData.status || null,
      Portal_Enabled: 1,
      Portal_Type: formData.portalType,
      Portal_Domain: formData.portalType === PORTAL_OWN_DOMAIN ? portalDomain : null,
      Owner_Company_Email: ownerCompanyEmail,
      ...(existingOwner ? {} : { Password: formData.portalPassword }),
      ...(selectedOwner && !ownerNeedsDomainLogin && isPrimaryCompany ? { Is_Primary_Company: true } : {}),
      ...(selectedOwner && ownerNeedsDomainLogin ? { Linked_Owner_User_Id: selectedOwner.User_Id } : {}),
      Created_By: createdBy,
      Updated_By: createdBy,
      services: numericServiceIds,
      ...(customServiceNames.length > 0 && { custom_services: customServiceNames }),
      Website: formData.website || null,
      Website_Domain_Name: formData.websiteDomainName || null,
      Com_Lang: (formData.communicationLanguage || "").trim() || null,
      Is_Quote_Sent: formData.isQuoteSent ? 1 : 0,
      Address_Line_1: formData.companyAddressLine1 || null,
      Address_Line_2: formData.companyAddressLine2 || null,
      City_Id: selectedCompanyCity ? selectedCompanyCity.city_id : null,
      State_Id: selectedCompanyState ? selectedCompanyState.state_id : selectedCompanyStateId,
      Country_Id: selectedCompanyCountry ? selectedCompanyCountry.country_id : selectedCompanyCountryId,
      Zip_Code: formData.companyZipCode || null,
      Notes: formData.notes || null
    };

    setSubmitting(true);
    try {
      await axiosInstance.post("/create-client", payload);
      alert("Tenant created successfully");
      if (onAddTenant) onAddTenant();
      if (onBack) onBack();
    } catch (err) {
      console.error("Error creating customer:", err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to create customer.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="addtenant-container">
      <button type="button" className="addtenant-back-link" onClick={onBack}>
        <FaArrowLeft /> Back to Tenants
      </button>
      <div className="issues-main-card">
        <form className="addtenant-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3 className="section-title addtenant-subsection-title">Company Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Company Name{" "}
                {String(formData.customerType || "").toLowerCase() === "organization" ? (
                  <span className="required">*</span>
                ) : null}
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                required={String(formData.customerType || "").toLowerCase() === "organization"}
              />
            </div>
            {selectedOwner && !ownerNeedsDomainLogin ? (
              <div className="form-group">
                <label>Primary Company</label>
                <label className={`primary-switch ${isPrimaryCompany ? "is-on" : ""}`}>
                  <input
                    type="checkbox"
                    checked={isPrimaryCompany}
                    onChange={(e) => setIsPrimaryCompany(e.target.checked)}
                  />
                  <span className="primary-switch__track" aria-hidden="true">
                    <span className="primary-switch__thumb" />
                  </span>
                  <span className="primary-switch__text">
                    {isPrimaryCompany
                      ? "This company"
                      : currentPrimaryCompany
                        ? `Keep ${currentPrimaryCompany}`
                        : "Not primary"}
                  </span>
                </label>
              </div>
            ) : null}
            <div className="form-group">
              <label>Business <span className="required">*</span></label>
              {domains.length > 0 ? (
                <div className="domain-filter-dropdown" ref={domainDropdownRef} style={{ minWidth: "100%" }}>
                  <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingDomains ? 0.6 : 1, cursor: loadingDomains ? "default" : "pointer" }} onClick={() => !loadingDomains && setDomainDropdownOpen((o) => !o)} role="button" tabIndex={loadingDomains ? -1 : 0} onKeyDown={(e) => { if (!loadingDomains && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setDomainDropdownOpen((o) => !o); } }}>
                    <span>{loadingDomains ? "Loading..." : (formData.domain ? (domains.find((d) => String(d.id) === String(formData.domain))?.name || "Select Business") : "Select Business")}</span>
                    <FaChevronDown className={`domain-filter-chevron ${domainDropdownOpen ? "open" : ""}`} />
                  </div>
                  {domainDropdownOpen && !loadingDomains && (
                    <div className="domain-filter-panel">
                      <input type="text" className="domain-filter-search" placeholder="Search..." value={domainSearchQuery} onChange={(e) => setDomainSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                      <div className="domain-filter-options">
                        <div className={`domain-filter-option ${!formData.domain ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: "" })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>Select Business</div>
                        {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).map((d) => (
                          <div key={d.id} className={`domain-filter-option ${String(formData.domain) === String(d.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: String(d.id), businessModel: d.defaultModel || p.businessModel })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>{d.name}</div>
                        ))}
                        {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No domains found</div>}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  required
                />
              )}
            </div>
            <div className="form-group">
              <label>Business Model <span className="required">*</span></label>
              <select
                name="businessModel"
                value={formData.businessModel}
                onChange={handleInputChange}
                required
              >
                <option value="">Select model</option>
                {BUSINESS_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Company Email</label>
              <input
                type="email"
                name="companyEmail"
                value={formData.companyEmail}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Company Phone</label>
              <div style={{ display: "flex", gap: "8px" }}>
                {renderCountryCodeSelect(companyPhoneCountryCode, setCompanyPhoneCountryCode)}
                <input
                  type="text"
                  name="companyPhone"
                  value={formData.companyPhone}
                  onChange={handleInputChange}
                  style={{ flex: 1, minWidth: 0 }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://www.example.com"
              />
            </div>
            <div className="form-group">
              <label>Domain Name</label>
              <input
                type="text"
                name="websiteDomainName"
                value={formData.websiteDomainName}
                onChange={handleInputChange}
                placeholder="example.com"
              />
            </div>
          </div>
        </div>

        <div className="form-section addtenant-section-divider">
          <h3 className="section-title addtenant-subsection-title">
            Company Address
            <FaInfoCircle
              className="services-info-icon"
              style={{ marginLeft: 6, verticalAlign: "middle" }}
              title="Office or business location for this tenant."
              aria-label="Office or business location for this tenant."
            />
          </h3>
          <div className="form-grid">
            <AddressSearch
              onSelect={addressAutofill.onSelect}
              viewParts={[
                formData.companyAddressLine1,
                formData.companyAddressLine2,
                formData.companyCity,
                formData.companyState,
                formData.companyZipCode,
                formData.companyCountry,
              ]}
            />
            <div className="form-group" style={{ gridColumnStart: 1 }}>
              <label>Address Line 1</label>
              <input
                type="text"
                name="companyAddressLine1"
                value={formData.companyAddressLine1}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                name="companyAddressLine2"
                value={formData.companyAddressLine2}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Country</label>
              <select
                name="companyCountry"
                value={formData.companyCountry}
                onChange={handleInputChange}
                disabled={loadingCountries}
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={`company-${country.country_id}`} value={country.country_name}>
                    {country.country_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>State</label>
              <select
                name="companyState"
                value={formData.companyState}
                onChange={handleInputChange}
                disabled={!selectedCompanyCountryId}
              >
                <option value="">Select State</option>
                {companyStates.map((state) => (
                  <option key={`company-${state.state_id}`} value={state.state_name}>
                    {state.state_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>City</label>
              <select
                name="companyCity"
                value={formData.companyCity}
                onChange={handleInputChange}
                disabled={!selectedCompanyStateId}
              >
                <option value="">Select City</option>
                {companyCities.map((city) => (
                  <option key={`company-${city.city_id}`} value={city.city_name}>
                    {city.city_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                name="companyZipCode"
                value={formData.companyZipCode}
                onChange={handleInputChange}
              />
            </div>
            {addressAutofill.note ? (
              <div className="form-group address-map-row">
                <small className="form-hint">{addressAutofill.note}</small>
              </div>
            ) : null}
          </div>
        </div>

        <div className="form-section addtenant-section-divider">
          <h3 className="section-title addtenant-subsection-title">Personal Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <div className="addtenant-owner-picker" ref={ownerPickerRef}>
                <input
                  type="text"
                  name="firstName"
                  className={selectedOwner ? "addtenant-owner-input is-linked" : "addtenant-owner-input"}
                  value={formData.firstName}
                  onChange={(e) => {
                    handleInputChange(e);
                    setOwnerDropdownOpen(true);
                  }}
                  onFocus={() => !selectedOwner && setOwnerDropdownOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setOwnerDropdownOpen(false);
                  }}
                  readOnly={Boolean(selectedOwner)}
                  placeholder="Select or create"
                  required
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={ownerDropdownOpen && !selectedOwner}
                  aria-autocomplete="list"
                />
                {selectedOwner ? (
                  <span className="addtenant-owner-tag">
                    <FaUserCheck aria-hidden="true" /> Existing
                    <button
                      type="button"
                      className="addtenant-owner-clear"
                      onClick={clearSelectedOwner}
                      title="Remove owner and start over"
                      aria-label="Remove owner and start over"
                    >
                      <FaTimes />
                    </button>
                  </span>
                ) : null}
                {!selectedOwner && ownerDropdownOpen && formData.firstName.trim() ? (
                  <div className="addtenant-owner-results" role="listbox">
                    <button
                      type="button"
                      className="addtenant-owner-option addtenant-owner-option--create"
                      onClick={() => setOwnerDropdownOpen(false)}
                    >
                      <span className="addtenant-owner-option__icon"><FaUserPlus /></span>
                      <span className="addtenant-owner-option__text">
                        <span className="addtenant-owner-option__name">
                          Create &ldquo;{formData.firstName.trim()}&rdquo;
                        </span>
                        <span className="addtenant-owner-option__meta">New owner</span>
                      </span>
                    </button>
                    {formData.firstName.trim().length >= 2 ? (
                      <>
                        <div className="addtenant-owner-group">Existing owners</div>
                        {ownerSearching ? (
                          <div className="addtenant-owner-empty">Searching...</div>
                        ) : ownerResults.length === 0 ? (
                          <div className="addtenant-owner-empty">No match</div>
                        ) : (
                          ownerResults.map((o) => (
                            <button
                              key={o.User_Id}
                              type="button"
                              className="addtenant-owner-option"
                              onClick={() => selectOwner(o)}
                            >
                              <span className="addtenant-owner-option__avatar">
                                {`${(o.First_Name || "?")[0]}${(o.Last_Name || "")[0] || ""}`.toUpperCase()}
                              </span>
                              <span className="addtenant-owner-option__text">
                                <span className="addtenant-owner-option__name">
                                  {`${o.First_Name || ""} ${o.Last_Name || ""}`.trim() || "—"}
                                </span>
                                <span className="addtenant-owner-option__meta">
                                  {[o.Company_Email, o.businesses?.map((b) => b.Company_Name).filter(Boolean).join(", ")]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                              </span>
                            </button>
                          ))
                        )}
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="form-group">
              <label>Last Name <span className="required">*</span></label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                disabled={lockedPersonFields.lastName}
              />
            </div>
            <div className="form-group">
              <label>Personal Email <span className="required">*</span></label>
              <input
                type="email"
                name="personalEmail"
                value={formData.personalEmail}
                onChange={handleInputChange}
                required
                disabled={lockedPersonFields.personalEmail}
              />
            </div>
            <div className="form-group">
              <label>Personal Phone <span className="required">*</span></label>
              <div style={{ display: "flex", gap: "8px" }}>
                {renderCountryCodeSelect(personalPhoneCountryCode, setPersonalPhoneCountryCode, {
                  disabled: lockedPersonFields.personalPhone,
                })}
                <input
                  type="text"
                  name="personalPhone"
                  value={formData.personalPhone}
                  onChange={handleInputChange}
                  required
                  disabled={lockedPersonFields.personalPhone}
                  style={{ flex: 1, minWidth: 0 }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Alternate phone</label>
              <div style={{ display: "flex", gap: "8px" }}>
                {renderCountryCodeSelect(altPhoneCountryCode, setAltPhoneCountryCode)}
                <input
                  type="text"
                  name="altPhone"
                  value={formData.altPhone}
                  onChange={handleInputChange}
                  style={{ flex: 1, minWidth: 0 }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Aadhar Number</label>
              <input
                type="text"
                name="aadhar"
                value={formData.aadhar}
                onChange={handleInputChange}
                placeholder="12-digit Aadhar"
                maxLength={12}
                inputMode="numeric"
              />
            </div>
          </div>
        </div>

        <PortalLoginFields
          portalType={formData.portalType}
          portalDomain={formData.portalDomain}
          suggestedDomain={formData.websiteDomainName || formData.website}
          ownerEmail={formData.ownerCompanyEmail}
          syncWithSuggested
          onChange={({ portalType, portalDomain }) =>
            setFormData((prev) => ({
              ...prev,
              portalType,
              portalDomain,
              ownerCompanyEmail: selectedOwner
                ? ownerLoginFor(selectedOwner, portalType, portalDomain)
                : domainAdminLogin(portalType, portalDomain) ||
                  (prev.ownerCompanyEmail.trim().toLowerCase() ===
                  domainAdminLogin(prev.portalType, prev.portalDomain)
                    ? ""
                    : autoOwnerEmail(prev.ownerCompanyEmail, portalType, portalDomain)),
            }))
          }
        />

        <div className="form-section addtenant-section-divider">
          <h3 className="section-title addtenant-subsection-title">Account</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Owner Login Email <span className="required">*</span>
                <FaInfoCircle
                  className="services-info-icon"
                  style={{ marginLeft: 6, verticalAlign: "middle" }}
                  title={
                    domainAdminNote ||
                    `${OWNER_EMAIL_INFO} ${ownerEmailHint(formData.portalType, formData.portalDomain)}`
                  }
                  aria-label={
                    domainAdminNote ||
                    `${OWNER_EMAIL_INFO} ${ownerEmailHint(formData.portalType, formData.portalDomain)}`
                  }
                />
              </label>
              <input
                type="email"
                name="ownerCompanyEmail"
                value={formData.ownerCompanyEmail}
                onChange={handleInputChange}
                required
                readOnly={isDomainAdminLogin || (Boolean(selectedOwner) && !ownerNeedsDomainLogin)}
                title={
                  isDomainAdminLogin
                    ? "Created automatically from the portal domain."
                    : selectedOwner && !ownerNeedsDomainLogin
                      ? "Linked to the selected owner. Clear the owner in First Name to pick someone else."
                      : undefined
                }
                autoComplete="off"
                placeholder={ownerEmailPlaceholder(formData.portalType, formData.portalDomain)}
              />
              {ownerDomainError(formData.ownerCompanyEmail, formData.portalType, formData.portalDomain) ? (
                <small className="form-hint" style={{ color: "#dc3545" }}>
                  {ownerDomainError(formData.ownerCompanyEmail, formData.portalType, formData.portalDomain)}
                </small>
              ) : existingOwner ? (
                <small className="form-hint">{describeExistingOwner(existingOwner)}</small>
              ) : null}
            </div>
            {!existingOwner && (
              <OwnerPasswordField
                required
                value={formData.portalPassword}
                onChange={(portalPassword) => setFormData((prev) => ({ ...prev, portalPassword }))}
                loginEmail={formData.ownerCompanyEmail.trim()}
                portalUrl={`https://${portalHostFor(formData.portalType, formData.portalDomain)}`}
                recipientName={formData.firstName.trim()}
                recipientEmail={formData.personalEmail.trim()}
                recipientPhone={`${personalPhoneCountryCode}${formData.personalPhone}`}
              />
            )}
            <div className="form-group" style={{ gridColumnStart: 1 }}>
              <label>Tenant Type</label>
              <select
                name="customerType"
                value={formData.customerType}
                onChange={handleInputChange}
              >
                {CUSTOMER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                className="tenant-narrow-control"
                value={formData.status}
                onChange={handleInputChange}
              >
                {CUSTOMER_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Quote
                <FaInfoCircle
                  className="services-info-icon"
                  style={{ marginLeft: 6, verticalAlign: "middle" }}
                  title="Quotes sent from Edit Tenant are saved to the tenant and mark it as Quote Sent."
                  aria-label="Quotes sent from Edit Tenant are saved to the tenant and mark it as Quote Sent."
                />
              </label>
              <div className="tenant-quote-row">
                <button
                  type="button"
                  className="tenant-quote-btn"
                  onClick={() => navigate("/generate-quote", { state: { customerData: formData, type: "client" } })}
                >
                  Generate Quote
                </button>
              </div>
            </div>
            <div className="form-group" style={{ gridColumnStart: 1 }}>
              <label>Communication Language</label>
              <input
                type="text"
                name="communicationLanguage"
                value={formData.communicationLanguage}
                onChange={handleInputChange}
              />
            </div>
            <TenantSourceField
              value={formData.source}
              onChange={(source) => setFormData((prev) => ({ ...prev, source }))}
              controlClassName="tenant-narrow-control"
            />
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                style={{ resize: "vertical", minHeight: "60px" }}
              />
            </div>
          </div>
        </div>

        <div className="form-section addtenant-section-divider">
          <h2 className="section-title">Services <span className="required">*</span></h2>
          {loadingServices ? (
            <p>Loading services...</p>
          ) : (
            <ServicePicker
              categories={serviceCategories}
              value={formData.services}
              onChange={(services) => setFormData((prev) => ({ ...prev, services }))}
              customServices={customServices}
              onDeleteCustom={handleDeleteCustomService}
            />
          )}
        </div>

        <div className="addtenant-actions">
          <button type="submit" className="btn-add-tenant" disabled={submitting} style={{ maxWidth: '200px' }}>
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
      </div>
      {editingService && (
        <div className="addlead-modal-overlay" onClick={handleCancelEditService}>
          <div className="addlead-modal addlead-add-service-modal" onClick={e => e.stopPropagation()}>
            <h3>{isAddingServiceToCategory ? `Add Service to ${serviceCategories.find(c => c.category_id === isAddingServiceToCategory)?.category_name || "Category"}` : "Edit Service"}</h3>
            <div className="form-group">
              <label>Service Name</label>
              <input
                type="text"
                value={editingService.name}
                onChange={e => setEditingService({ ...editingService, name: e.target.value })}
              
              />
            </div>
            <div className="addlead-modal-actions">
              <button type="button" className="btn-cancel" onClick={handleCancelEditService}>Cancel</button>
              <button type="button" className="btn-add-tenant" onClick={handleSaveService} disabled={!editingService.name?.trim()}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddTenant;
